import { useCallback, useEffect, useRef, useState } from "react";

import { onboardingAgentPreferencesService } from "../../onboardingAgentPreferencesService";
import { onboardingAgentTrackingService } from "../../onboardingAgentTrackingService";
import { ONBOARDING_AGENT_TRIGGER_SIZE } from "../constants";
import {
    createDefaultTriggerPosition,
    createVisiblePanelPosition,
    getCompactPanelHeight,
    getExpandedPanelHeight,
    getViewportSize,
    reflowPositionForViewportResize,
} from "../helpers/OnboardingAgentPosition.helpers";
import type { OnboardingAgentViewportPosition } from "../OnboardingAgent.types";

type UseOnboardingAgentParams = {
    readonly getThreadId?: () => string | undefined;
};

export const useOnboardingAgent = ({ getThreadId }: UseOnboardingAgentParams = {}) => {
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [isFirstOpen, setIsFirstOpen] = useState(false);
    const [isPanelExpanded, setIsPanelExpanded] = useState(false);
    const [shouldAnimatePanelPosition, setShouldAnimatePanelPosition] = useState(false);
    const [shouldAnimateTriggerPosition, setShouldAnimateTriggerPosition] = useState(false);
    const [triggerPosition, setTriggerPosition] = useState(createDefaultTriggerPosition);
    const [panelPosition, setPanelPosition] = useState(() =>
        createVisiblePanelPosition(createDefaultTriggerPosition()),
    );
    const triggerPositionBeforeExpandRef = useRef<OnboardingAgentViewportPosition | null>(null);
    const viewportSizeRef = useRef(getViewportSize());
    const panelMountRef = useRef<HTMLDivElement>(null);

    const getCurrentPanelHeight = useCallback(
        (): number => (isPanelExpanded ? getExpandedPanelHeight() : getCompactPanelHeight()),
        [isPanelExpanded],
    );

    const resetPositions = useCallback((): void => {
        const defaultTriggerPosition = createDefaultTriggerPosition();

        setTriggerPosition(defaultTriggerPosition);
        setPanelPosition(createVisiblePanelPosition(defaultTriggerPosition));
    }, []);

    const handleOpenPanel = useCallback(() => {
        const shouldShowFirstOpenGreeting = onboardingAgentPreferencesService.isFirstOpen();

        setShouldAnimatePanelPosition(false);
        setShouldAnimateTriggerPosition(false);
        setPanelPosition(createVisiblePanelPosition(triggerPosition));
        setIsFirstOpen(shouldShowFirstOpenGreeting);
        if (shouldShowFirstOpenGreeting) {
            void onboardingAgentPreferencesService.markOpened();
        }
        onboardingAgentTrackingService.trackChatbotOpen(getThreadId?.());
        setIsPanelOpen(true);
    }, [getThreadId, triggerPosition]);

    const handleClosePanel = useCallback(() => {
        setIsPanelOpen(false);
        setShouldAnimatePanelPosition(false);
        setShouldAnimateTriggerPosition(false);
    }, []);

    const handlePanelExited = useCallback((): void => {
        setShouldAnimateTriggerPosition(true);
        triggerPositionBeforeExpandRef.current = null;
        setIsPanelExpanded(false);
        resetPositions();
    }, [resetPositions]);

    const handleToggleExpand = useCallback((): void => {
        if (!isPanelOpen) {
            return;
        }

        if (!isPanelExpanded) {
            triggerPositionBeforeExpandRef.current = triggerPosition;
            const defaultTrigger = createDefaultTriggerPosition();
            const expandedHeight = getExpandedPanelHeight();
            setShouldAnimatePanelPosition(true);
            setTriggerPosition(defaultTrigger);
            setPanelPosition(createVisiblePanelPosition(defaultTrigger, expandedHeight));
            setIsPanelExpanded(true);
            onboardingAgentTrackingService.trackExpand(true, getThreadId?.());
            return;
        }

        const restoredTrigger = triggerPositionBeforeExpandRef.current ?? triggerPosition;
        triggerPositionBeforeExpandRef.current = null;
        setShouldAnimatePanelPosition(true);
        setTriggerPosition(restoredTrigger);
        setPanelPosition(createVisiblePanelPosition(restoredTrigger, getCompactPanelHeight()));
        setIsPanelExpanded(false);
        onboardingAgentTrackingService.trackExpand(false, getThreadId?.());
    }, [getThreadId, isPanelExpanded, isPanelOpen, triggerPosition]);

    useEffect(() => {
        const handleResize = (): void => {
            const previousViewport = viewportSizeRef.current;
            const nextViewport = getViewportSize();

            viewportSizeRef.current = nextViewport;

            if (previousViewport.width === nextViewport.width && previousViewport.height === nextViewport.height) {
                return;
            }

            setShouldAnimatePanelPosition(false);
            setShouldAnimateTriggerPosition(false);

            setTriggerPosition((position) => {
                const nextTriggerPosition = reflowPositionForViewportResize(
                    position,
                    previousViewport,
                    ONBOARDING_AGENT_TRIGGER_SIZE,
                    ONBOARDING_AGENT_TRIGGER_SIZE,
                );

                if (isPanelOpen) {
                    setPanelPosition(createVisiblePanelPosition(nextTriggerPosition, getCurrentPanelHeight()));
                }

                return nextTriggerPosition;
            });
        };

        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, [getCurrentPanelHeight, isPanelOpen]);

    return {
        isPanelOpen,
        isFirstOpen,
        isPanelExpanded,
        shouldAnimatePanelPosition,
        shouldAnimateTriggerPosition,
        panelMountRef,
        triggerPosition,
        panelPosition,
        handleOpenPanel,
        handleClosePanel,
        handlePanelExited,
        handleToggleExpand,
    };
};
